import { AUDIT_TYPE } from "@/services/audit/types"

export default {
  overview: {
    title: "Activity Log",
    subtitle: "View all system activities and changes",
  },
  // Stands in for the name when the account that made the change no longer exists. The entry
  // itself stays: the trail has to outlive the account, or it records nothing.
  deleted_user: "Deleted user",
  // Instead of the name where Lewara itself made the change (a nightly run, no account).
  system_user: "Lewara",
  // The heading of a bundled row: entries of the same wording on one day. A kind without its own plural
  // below reads "3× Category updated".
  bundle: "{count}× {text}",
  bundles: {
    [AUDIT_TYPE.CREATE_CONSTRUCTION_REPORT]: "{count} [[cr.pl]] created",
    [AUDIT_TYPE.UPDATE_CONSTRUCTION_REPORT]: "{count} [[cr.pl]] updated",
    [AUDIT_TYPE.DELETE_CONSTRUCTION_REPORT]: "{count} [[cr.pl]] deleted",
    [AUDIT_TYPE.PRINT_CONSTRUCTION_REPORT]: "{count} [[cr.pl]] printed",
    [AUDIT_TYPE.CREATE_HOURLY_WAGE_REPORT]: "{count} [[hwr.pl]] created",
    [AUDIT_TYPE.UPDATE_HOURLY_WAGE_REPORT]: "{count} [[hwr.pl]] updated",
    [AUDIT_TYPE.DELETE_HOURLY_WAGE_REPORT]: "{count} [[hwr.pl]] deleted",
    [AUDIT_TYPE.BILL_HOURLY_WAGE_REPORT]: "{count} [[hwr.pl]] billed",
    [AUDIT_TYPE.PRINT_HOURLY_WAGE_REPORT]: "{count} [[hwr.pl]] printed",
    [AUDIT_TYPE.CREATE_HOURLY_WAGE_INVOICE]: "{count} [[hwi.pl]] created",
    [AUDIT_TYPE.UPDATE_HOURLY_WAGE_INVOICE]: "{count} [[hwi.pl]] updated",
    [AUDIT_TYPE.CREATE_MACHINE_OBJECT_PRICE]: "{count} project prices entered",
    [AUDIT_TYPE.UPDATE_MACHINE_OBJECT_PRICE]: "{count} project prices changed",
    [AUDIT_TYPE.DELETE_MACHINE_OBJECT_PRICE]: "{count} project prices removed",
    [AUDIT_TYPE.CREATE_MATERIAL_OBJECT_PRICE]: "{count} project prices entered",
    [AUDIT_TYPE.UPDATE_MATERIAL_OBJECT_PRICE]: "{count} project prices changed",
    [AUDIT_TYPE.DELETE_MATERIAL_OBJECT_PRICE]: "{count} project prices removed",
    [AUDIT_TYPE.CREATE_MACHINE_PURCHASE_PRICE_REVISION]: "{count} prices entered",
    [AUDIT_TYPE.UPDATE_MACHINE_PURCHASE_PRICE_REVISION]: "{count} prices corrected",
    [AUDIT_TYPE.DELETE_MACHINE_PURCHASE_PRICE_REVISION]: "{count} prices removed",
    [AUDIT_TYPE.CREATE_MATERIAL_PURCHASE_PRICE_REVISION]: "{count} prices entered",
    [AUDIT_TYPE.UPDATE_MATERIAL_PURCHASE_PRICE_REVISION]: "{count} prices corrected",
    [AUDIT_TYPE.DELETE_MATERIAL_PURCHASE_PRICE_REVISION]: "{count} prices removed",
    [AUDIT_TYPE.CREATE_RENTAL]: "{count} rentals started",
    [AUDIT_TYPE.UPDATE_RENTAL]: "{count} rentals changed",
    [AUDIT_TYPE.DELETE_RENTAL]: "{count} rentals removed",
    [AUDIT_TYPE.CREATE_CHANGE_ORDER]: "{count} change orders created",
    [AUDIT_TYPE.UPDATE_CHANGE_ORDER]: "{count} change orders updated",
    [AUDIT_TYPE.DELETE_CHANGE_ORDER]: "{count} change orders deleted",
    [AUDIT_TYPE.CREATE_SERVICE_CATALOG_ITEM]: "{count} service catalogue entries created",
    [AUDIT_TYPE.UPDATE_SERVICE_CATALOG_ITEM]: "{count} service catalogue entries updated",
    [AUDIT_TYPE.DELETE_SERVICE_CATALOG_ITEM]: "{count} service catalogue entries deleted",
    [AUDIT_TYPE.CREATE_SERVICE_PROGRESS]: "{count} progress readings recorded",
    [AUDIT_TYPE.UPDATE_SERVICE_PROGRESS]: "{count} progress readings adjusted",
    [AUDIT_TYPE.DELETE_SERVICE_PROGRESS]: "{count} progress readings deleted",
    [AUDIT_TYPE.CREATE_ASSIGNMENT]: "{count} calendar assignments created",
    [AUDIT_TYPE.UPDATE_ASSIGNMENT]: "{count} calendar assignments updated",
    [AUDIT_TYPE.DELETE_ASSIGNMENT]: "{count} calendar assignments deleted",
    [AUDIT_TYPE.CREATE_TIME_ENTRY]: "{count} time entries recorded",
    [AUDIT_TYPE.UPDATE_TIME_ENTRY]: "{count} time entries updated",
    [AUDIT_TYPE.DELETE_TIME_ENTRY]: "{count} time entries deleted",
    [AUDIT_TYPE.CREATE_PROJECT]: "{count} projects created",
    [AUDIT_TYPE.UPDATE_PROJECT]: "{count} projects updated",
    [AUDIT_TYPE.CREATE_CUSTOMER]: "{count} customers created",
    [AUDIT_TYPE.UPDATE_CUSTOMER]: "{count} customers updated",
    [AUDIT_TYPE.CREATE_MATERIAL]: "{count} materials created",
    [AUDIT_TYPE.UPDATE_MATERIAL]: "{count} materials updated",
    [AUDIT_TYPE.CREATE_MACHINE]: "{count} machines created",
    [AUDIT_TYPE.UPDATE_MACHINE]: "{count} machines updated",
    [AUDIT_TYPE.CREATE_USER]: "{count} employees created",
    [AUDIT_TYPE.UPDATE_USER]: "{count} employees updated",
  },
  values: {
    unset: "Unset",
    amount: "Amount",
    boolIsTrue: "Marked",
    boolIsFalse: "Unmarked",
    workspace_default: "Workspace default",
    pdf_template: "workspace template",
    // A map of many values, e.g. a phase's capacity per day.
    map_values: "{count} values",
  },
  // Technical fields the persistence layer writes on every save. They show up in nearly every list
  // of changes and belong to no one feature, hence here rather than in a feature file.
  properties: {
    last_modified_at: "Last changed at",
    last_modified_by: "Last changed by",
    // Other names of a machine or material ("crawler excavator") the voice day report recognises too. A list: the log
    // names how many there were and are.
    aliases: "Other names",
    // The fields of one file, the same on every record with a file tab.
    file: {
      title: "File name",
      tags: "Tags",
      tag: "Tag",
      visible: "Show in PDF",
      print_original: "Print in original quality",
      description: "Comment",
    },
    custom_field_value: "Custom field value",
    // An entry of the speech recognition's vocabulary (the app's voice day report). A list arrives as
    // its count, hence "Terms" with an amount rather than the words themselves.
    day_submission: {
      date: "Day",
      project_new_name: "Site as named",
      status: "Status",
      dismiss_note: "Dismissal note",
    },
    voice_vocabulary: {
      terms: "Terms",
      keyterm: "Expected by the speech recognition",
      note: "Note",
    },
  },
  types: {
    [AUDIT_TYPE.CREATE_TENANT]: {
      text: "Tenant created",
      subtext: "Tenant was created",
    },
    [AUDIT_TYPE.UPDATE_TENANT]: {
      text: "Tenant updated",
      subtext: "{userFullName} updated the Tenant Settings",
    },

    [AUDIT_TYPE.CREATE_CATEGORY]: {
      text: "Category created",
      subtext: "{userFullName} created the Category {name}",
    },
    [AUDIT_TYPE.UPDATE_CATEGORY]: {
      text: "Category updated",
      subtext: "{userFullName} updated Category {name}",
    },
    [AUDIT_TYPE.ARCHIVE_CATEGORY]: {
      text: "Category archived",
      subtext: "{userFullName} archived Category {name}",
    },
    [AUDIT_TYPE.RESTORE_CATEGORY]: {
      text: "Category restored",
      subtext: "{userFullName} restored Category {name}",
    },
    [AUDIT_TYPE.DELETE_CATEGORY]: {
      text: "Category deleted",
      subtext: "{userFullName} deleted Category {name}",
    },

    [AUDIT_TYPE.CREATE_MACHINE]: {
      text: "machine created",
      subtext: "{userFullName} created machine {name}",
    },
    [AUDIT_TYPE.UPDATE_MACHINE]: {
      text: "machine updated",
      subtext: "{userFullName} updated machine {name}",
    },
    [AUDIT_TYPE.ARCHIVE_MACHINE]: {
      text: "machine archived",
      subtext: "{userFullName} archived machine {name}",
    },
    [AUDIT_TYPE.RESTORE_MACHINE]: {
      text: "machine restored",
      subtext: "{userFullName} restored machine {name}",
    },
    [AUDIT_TYPE.DELETE_MACHINE]: {
      text: "machine deleted",
      subtext: "{userFullName} deleted machine {name}",
    },

    [AUDIT_TYPE.CREATE_MATERIAL]: {
      text: "Material created",
      subtext: "{userFullName} created Material {name}",
    },
    [AUDIT_TYPE.UPDATE_MATERIAL]: {
      text: "Material updated",
      subtext: "{userFullName} updated Material {name}",
    },
    [AUDIT_TYPE.ARCHIVE_MATERIAL]: {
      text: "Material archived",
      subtext: "{userFullName} archived Material {name}",
    },
    [AUDIT_TYPE.RESTORE_MATERIAL]: {
      text: "Material restored",
      subtext: "{userFullName} restored Material {name}",
    },
    [AUDIT_TYPE.DELETE_MATERIAL]: {
      text: "Material deleted",
      subtext: "{userFullName} deleted Material {name}",
    },

    [AUDIT_TYPE.CREATE_ADDITIONAL_EFFORT]: {
      text: "Additional Effort created",
      subtext: "{userFullName} created additional effort {name}",
    },
    [AUDIT_TYPE.UPDATE_ADDITIONAL_EFFORT]: {
      text: "Additional Effort updated",
      subtext: "{userFullName} updated additional effort {name}",
    },
    [AUDIT_TYPE.ARCHIVE_ADDITIONAL_EFFORT]: {
      text: "Additional Effort archived",
      subtext: "{userFullName} archived additional effort {name}",
    },
    [AUDIT_TYPE.RESTORE_ADDITIONAL_EFFORT]: {
      text: "Additional Effort restored",
      subtext: "{userFullName} restored additional effort {name}",
    },
    [AUDIT_TYPE.DELETE_ADDITIONAL_EFFORT]: {
      text: "Additional Effort deleted",
      subtext: "{userFullName} deleted additional effort {name}",
    },

    [AUDIT_TYPE.CREATE_CONSTRUCTION_REPORT]: {
      text: "[[CR]] created",
      subtext: '{userFullName} created [[CR]] "{name}" in project "{project}"',
    },
    [AUDIT_TYPE.UPDATE_CONSTRUCTION_REPORT]: {
      text: "[[CR]] updated",
      subtext: '{userFullName} updated [[CR]] "{name}" in project "{project}"',
    },
    [AUDIT_TYPE.DELETE_CONSTRUCTION_REPORT]: {
      text: "[[CR]] deleted",
      subtext: '{userFullName} deleted [[CR]] "{name}" in project "{project}"',
    },
    [AUDIT_TYPE.PRINT_CONSTRUCTION_REPORT]: {
      text: "[[CR]] printed",
      subtext: '{userFullName} printed [[CR]] "{name}" in project "{project}"',
    },

    [AUDIT_TYPE.CREATE_CONTACT_POSITION]: {
      text: "Contact Position created",
      subtext: "{userFullName} created Contact Position {name}",
    },
    [AUDIT_TYPE.UPDATE_CONTACT_POSITION]: {
      text: "Contact Position updated",
      subtext: "{userFullName} updated Contact Position {name}",
    },
    [AUDIT_TYPE.DELETE_CONTACT_POSITION]: {
      text: "Contact Position deleted",
      subtext: "{userFullName} deleted Contact Position {name}",
    },

    [AUDIT_TYPE.CREATE_CUSTOMER]: {
      text: "Customer created",
      subtext: "{userFullName} created Customer {name}",
    },
    [AUDIT_TYPE.UPDATE_CUSTOMER]: {
      text: "Customer updated",
      subtext: "{userFullName} updated Customer {name}",
    },
    [AUDIT_TYPE.ARCHIVE_CUSTOMER]: {
      text: "Customer archived",
      subtext: "{userFullName} archived Customer {name}",
    },
    [AUDIT_TYPE.RESTORE_CUSTOMER]: {
      text: "Customer restored",
      subtext: "{userFullName} restored Customer {name}",
    },
    [AUDIT_TYPE.CREATE_SUPPLIER]: {
      text: "Supplier created",
      subtext: '{userFullName} created the supplier "{name}"',
    },
    [AUDIT_TYPE.UPDATE_SUPPLIER]: {
      text: "Supplier updated",
      subtext: '{userFullName} updated the supplier "{name}"',
    },
    [AUDIT_TYPE.DELETE_SUPPLIER]: {
      text: "Supplier deleted",
      subtext: '{userFullName} deleted the supplier "{name}"',
    },
    [AUDIT_TYPE.ARCHIVE_SUPPLIER]: {
      text: "Supplier archived",
      subtext: '{userFullName} archived the supplier "{name}"',
    },
    [AUDIT_TYPE.RESTORE_SUPPLIER]: {
      text: "Supplier restored",
      subtext: '{userFullName} restored the supplier "{name}"',
    },
    [AUDIT_TYPE.CREATE_SUPPLIER_KIND]: {
      text: "Supplier kind created",
      subtext: '{userFullName} created the kind "{name}"',
    },
    [AUDIT_TYPE.UPDATE_SUPPLIER_KIND]: {
      text: "Supplier kind updated",
      subtext: '{userFullName} updated the kind "{name}"',
    },
    [AUDIT_TYPE.DELETE_SUPPLIER_KIND]: {
      text: "Supplier kind deleted",
      subtext: '{userFullName} deleted the kind "{name}"',
    },
    [AUDIT_TYPE.CREATE_MACHINE_PURCHASE_PRICE_REVISION]: {
      text: "Price entered",
      subtext: '{userFullName} entered the price of "{supplier}" for "{item}" from {date}',
    },
    [AUDIT_TYPE.UPDATE_MACHINE_PURCHASE_PRICE_REVISION]: {
      text: "Price corrected",
      subtext: '{userFullName} corrected the price of "{supplier}" for "{item}" from {date}',
    },
    [AUDIT_TYPE.DELETE_MACHINE_PURCHASE_PRICE_REVISION]: {
      text: "Price removed",
      subtext: '{userFullName} removed the price of "{supplier}" for "{item}" from {date}',
    },
    [AUDIT_TYPE.CREATE_MATERIAL_PURCHASE_PRICE_REVISION]: {
      text: "Price entered",
      subtext: '{userFullName} entered the price of "{supplier}" for "{item}" from {date}',
    },
    [AUDIT_TYPE.UPDATE_MATERIAL_PURCHASE_PRICE_REVISION]: {
      text: "Price corrected",
      subtext: '{userFullName} corrected the price of "{supplier}" for "{item}" from {date}',
    },
    [AUDIT_TYPE.DELETE_MATERIAL_PURCHASE_PRICE_REVISION]: {
      text: "Price removed",
      subtext: '{userFullName} removed the price of "{supplier}" for "{item}" from {date}',
    },
    [AUDIT_TYPE.CREATE_MACHINE_OBJECT_PRICE]: {
      text: "Project price entered",
      subtext: "{userFullName} entered a project price",
      subtext_named: '{userFullName} entered the project price for "{item}"',
    },
    [AUDIT_TYPE.UPDATE_MACHINE_OBJECT_PRICE]: {
      text: "Project price changed",
      subtext: "{userFullName} changed a project price",
      subtext_named: '{userFullName} changed the project price for "{item}"',
    },
    [AUDIT_TYPE.DELETE_MACHINE_OBJECT_PRICE]: {
      text: "Project price removed",
      subtext: "{userFullName} removed a project price",
      subtext_named: '{userFullName} removed the project price for "{item}"',
    },
    [AUDIT_TYPE.CREATE_MATERIAL_OBJECT_PRICE]: {
      text: "Project price entered",
      subtext: "{userFullName} entered a project price",
      subtext_named: '{userFullName} entered the project price for "{item}"',
    },
    [AUDIT_TYPE.UPDATE_MATERIAL_OBJECT_PRICE]: {
      text: "Project price changed",
      subtext: "{userFullName} changed a project price",
      subtext_named: '{userFullName} changed the project price for "{item}"',
    },
    [AUDIT_TYPE.DELETE_MATERIAL_OBJECT_PRICE]: {
      text: "Project price removed",
      subtext: "{userFullName} removed a project price",
      subtext_named: '{userFullName} removed the project price for "{item}"',
    },
    [AUDIT_TYPE.CREATE_RENTAL]: {
      text: "Rental started",
      subtext: '{userFullName} rented "{machine}" from "{supplier}" as of {date}',
    },
    [AUDIT_TYPE.UPDATE_RENTAL]: {
      text: "Rental changed",
      subtext: '{userFullName} changed the rental of "{machine}" as of {date}',
    },
    [AUDIT_TYPE.DELETE_RENTAL]: {
      text: "Rental removed",
      subtext: '{userFullName} removed the rental of "{machine}" as of {date}',
    },
    [AUDIT_TYPE.CREATE_SUPPLIER_ACCESSORY]: {
      text: "Accessory added",
      subtext: '{userFullName} added the accessory "{name}" at "{supplier}"',
      subtext_machine: '{userFullName} added the accessory "{name}" for "{machine}" at "{supplier}"',
    },
    [AUDIT_TYPE.UPDATE_SUPPLIER_ACCESSORY]: {
      text: "Accessory changed",
      subtext: '{userFullName} changed the accessory "{name}" at "{supplier}"',
      subtext_machine: '{userFullName} changed the accessory "{name}" for "{machine}" at "{supplier}"',
    },
    [AUDIT_TYPE.DELETE_SUPPLIER_ACCESSORY]: {
      text: "Accessory removed",
      subtext: '{userFullName} removed the accessory "{name}" at "{supplier}"',
      subtext_machine: '{userFullName} removed the accessory "{name}" for "{machine}" at "{supplier}"',
    },
    [AUDIT_TYPE.DELETE_CUSTOMER]: {
      text: "Customer deleted",
      subtext: "{userFullName} deleted Customer {name}",
    },

    [AUDIT_TYPE.CREATE_FILE_TAG]: {
      text: "File-tag created",
      subtext: "{userFullName} created File-tag {name}",
    },

    [AUDIT_TYPE.CREATE_HOURLY_WAGE_INVOICE]: {
      text: "[[HWI]] created",
      subtext: '{userFullName} created [[HWI]] "{name}" for project "{project}"',
    },
    [AUDIT_TYPE.UPDATE_HOURLY_WAGE_INVOICE]: {
      text: "[[HWI]] updated",
      subtext: '{userFullName} updated [[HWI]] "{name}" for project "{project}"',
    },

    [AUDIT_TYPE.CREATE_HOURLY_WAGE_REPORT]: {
      text: "[[HWR]] created",
      subtext: '{userFullName} created [[HWR]] "{name}" in project "{project}"',
    },
    [AUDIT_TYPE.UPDATE_HOURLY_WAGE_REPORT]: {
      text: "[[HWR]] updated",
      subtext: '{userFullName} updated [[HWR]] "{name}" in project "{project}"',
    },
    [AUDIT_TYPE.DELETE_HOURLY_WAGE_REPORT]: {
      text: "[[HWR]] deleted",
      subtext: '{userFullName} deleted [[HWR]] "{name}" in project "{project}"',
    },
    [AUDIT_TYPE.BILL_HOURLY_WAGE_REPORT]: {
      text: "[[HWR]] billed",
      subtext: '{userFullName} billed [[HWR]] "{name}" in project "{project}"',
    },
    [AUDIT_TYPE.PRINT_HOURLY_WAGE_REPORT]: {
      text: "[[HWR]] printed",
      subtext: '{userFullName} printed [[HWR]] "{name}" in project "{project}"',
    },

    [AUDIT_TYPE.CREATE_HOURLY_WAGE_REPORT_TAG]: {
      text: "[[HWR]] tag created",
      subtext: "{userFullName} created [[HWR]] tag {name}",
    },
    [AUDIT_TYPE.DELETE_HOURLY_WAGE_REPORT_TAG]: {
      text: "[[HWR]] tag deleted",
      subtext: "{userFullName} deleted [[HWR]] tag {name}",
    },

    [AUDIT_TYPE.CREATE_PACKET]: {
      text: "Packet created",
      subtext: "{userFullName} created Packet {name}",
    },
    [AUDIT_TYPE.UPDATE_PACKET]: {
      text: "Packet updated",
      subtext: "{userFullName} updated Packet {name}",
    },
    [AUDIT_TYPE.DELETE_PACKET]: {
      text: "Packet deleted",
      subtext: "{userFullName} deleted Packet {name}",
    },

    [AUDIT_TYPE.CREATE_USER_CUSTOM_FIELD]: {
      text: "User Custom Field created",
      subtext: "{userFullName} created a custom field",
      subtext_named: '{userFullName} created custom field "{name}"',
    },

    [AUDIT_TYPE.CREATE_USER_POSITION]: {
      text: "Employee-position created",
      subtext: "{userFullName} created Employee-position {name}",
    },
    [AUDIT_TYPE.UPDATE_USER_POSITION]: {
      text: "Employee-position updated",
      subtext: "{userFullName} updated Employee-position {name}",
    },
    [AUDIT_TYPE.ARCHIVE_USER_POSITION]: {
      text: "Employee-position archived",
      subtext: "{userFullName} archived Employee-position {name}",
    },
    [AUDIT_TYPE.RESTORE_USER_POSITION]: {
      text: "Employee-position restored",
      subtext: "{userFullName} restored Employee-position {name}",
    },
    [AUDIT_TYPE.DELETE_USER_POSITION]: {
      text: "Employee-position deleted",
      subtext: "{userFullName} deleted Employee-position {name}",
    },

    [AUDIT_TYPE.CREATE_USER]: {
      text: "Employee created",
      subtext: "{userFullName} created Employee {name}",
    },
    [AUDIT_TYPE.UPDATE_USER]: {
      text: "Employee updated",
      subtext: "{userFullName} updated Employee {name}",
    },
    [AUDIT_TYPE.ARCHIVE_USER]: {
      text: "Employee archived",
      subtext: "{userFullName} archived Employee {name}",
    },
    [AUDIT_TYPE.RESTORE_USER]: {
      text: "Employee restored",
      subtext: "{userFullName} restored Employee {name}",
    },
    [AUDIT_TYPE.DELETE_USER]: {
      text: "Employee deleted",
      subtext: "{userFullName} deleted an employee",
      subtext_named: "{userFullName} deleted Employee {name}",
    },

    [AUDIT_TYPE.CREATE_PROJECT]: {
      text: "Project created",
      subtext: "{userFullName} created Project {name}",
    },
    [AUDIT_TYPE.UPDATE_PROJECT]: {
      text: "Project updated",
      subtext: "{userFullName} updated a project",
      subtext_named: "{userFullName} updated Project {name}",
    },
    [AUDIT_TYPE.ARCHIVE_PROJECT]: {
      text: "Project archived",
      subtext: "{userFullName} archived a project",
      subtext_named: "{userFullName} archived Project {name}",
    },
    [AUDIT_TYPE.RESTORE_PROJECT]: {
      text: "Project restored",
      subtext: "{userFullName} restored a project",
      subtext_named: "{userFullName} restored Project {name}",
    },
    [AUDIT_TYPE.DELETE_PROJECT]: {
      text: "Project deleted",
      subtext: "{userFullName} deleted Project {name}",
    },
    [AUDIT_TYPE.DUPLICATE_PROJECT]: {
      text: "Project duplicated",
      subtext: "{userFullName} created a project as a copy",
      subtext_named: '{userFullName} created project "{name}" as a copy',
    },

    // The API ships no payload for montage elements — which position was touched is told by the
    // changes listed under the entry.
    [AUDIT_TYPE.CREATE_MONTAGE_ELEMENT]: {
      text: "Montage position created",
      subtext: "{userFullName} created a position in the parts list",
    },
    [AUDIT_TYPE.UPDATE_MONTAGE_ELEMENT]: {
      text: "Montage position updated",
      subtext: "{userFullName} updated a position in the parts list",
    },
    [AUDIT_TYPE.DELETE_MONTAGE_ELEMENT]: {
      text: "Montage position deleted",
      subtext: "{userFullName} deleted a position from the parts list",
    },
    // Catalogue rows and change orders name themselves: the Ordnungszahl and the change order's number
    // are what the row is called on site and towards the client. `subtext` is everywhere the plain
    // sentence that needs no payload; the enriched variants beside it are used only when the payload
    // carries every field — a sentence with empty quotes in it is worse than one that leaves the number
    // out. Whether a row is a title or a position is in the payload, so the sentence says which.
    [AUDIT_TYPE.CREATE_SERVICE_CATALOG_ITEM]: {
      text: "Service catalogue entry created",
      subtext: "{userFullName} created a title or position in the service catalogue",
      subtext_position: '{userFullName} created position {identifier} "{text}" in project "{project}"',
      subtext_title: '{userFullName} created title {identifier} "{text}" in project "{project}"',
    },
    [AUDIT_TYPE.UPDATE_SERVICE_CATALOG_ITEM]: {
      text: "Service catalogue entry updated",
      subtext: "{userFullName} updated a title or position in the service catalogue",
      subtext_position: '{userFullName} updated position {identifier} "{text}" in project "{project}"',
      subtext_title: '{userFullName} updated title {identifier} "{text}" in project "{project}"',
    },
    [AUDIT_TYPE.DELETE_SERVICE_CATALOG_ITEM]: {
      text: "Service catalogue entry deleted",
      subtext: "{userFullName} deleted a title or position from the service catalogue",
      subtext_position: '{userFullName} deleted position {identifier} "{text}" in project "{project}"',
      subtext_title: '{userFullName} deleted title {identifier} "{text}" in project "{project}"',
    },
    [AUDIT_TYPE.CREATE_CHANGE_ORDER]: {
      text: "Change order created",
      subtext: "{userFullName} created a change order",
      subtext_named: '{userFullName} created change order {number} "{title}" in project "{project}"',
    },
    [AUDIT_TYPE.UPDATE_CHANGE_ORDER]: {
      text: "Change order updated",
      subtext: "{userFullName} updated a change order",
      subtext_named: '{userFullName} updated change order {number} "{title}" in project "{project}"',
    },
    [AUDIT_TYPE.DELETE_CHANGE_ORDER]: {
      text: "Change order deleted",
      subtext: "{userFullName} deleted a change order",
      subtext_named: '{userFullName} deleted change order {number} "{title}" in project "{project}"',
    },
    // One progress reading per entry, also when a cutoff date was recorded for many positions at once.
    [AUDIT_TYPE.CREATE_SERVICE_PROGRESS]: {
      text: "Progress recorded",
      subtext: "{userFullName} recorded a progress reading",
      subtext_named:
        '{userFullName} recorded the progress of position {identifier} "{text}" in project "{project}" as of {date}: {quantity}',
      subtext_opening:
        '{userFullName} recorded the opening reading of position {identifier} "{text}" in project "{project}": {quantity}',
    },
    [AUDIT_TYPE.UPDATE_SERVICE_PROGRESS]: {
      text: "Progress adjusted",
      subtext: "{userFullName} adjusted a progress reading",
      subtext_named:
        '{userFullName} adjusted the progress of position {identifier} "{text}" in project "{project}" as of {date}: {quantity}',
      subtext_opening:
        '{userFullName} adjusted the opening reading of position {identifier} "{text}" in project "{project}": {quantity}',
    },
    [AUDIT_TYPE.DELETE_SERVICE_PROGRESS]: {
      text: "Progress reading deleted",
      subtext: "{userFullName} deleted a progress reading",
      subtext_named:
        '{userFullName} deleted the progress reading as of {date} of position {identifier} "{text}" in project "{project}"',
      subtext_opening:
        '{userFullName} deleted the opening reading of position {identifier} "{text}" in project "{project}"',
    },

    [AUDIT_TYPE.CREATE_ROLE]: {
      text: "Role created",
      subtext: "{userFullName} created Role {name}",
    },
    [AUDIT_TYPE.UPDATE_ROLE]: {
      text: "Role updated",
      subtext: "{userFullName} updated Role {name}",
    },
    [AUDIT_TYPE.DELETE_ROLE]: {
      text: "Role deleted",
      subtext: "{userFullName} deleted Role {name}",
    },
    [AUDIT_TYPE.CREATE_TEAM]: {
      text: "Team created",
      subtext: '{userFullName} created team "{name}"',
    },
    [AUDIT_TYPE.UPDATE_TEAM]: {
      text: "Team updated",
      subtext: '{userFullName} updated Team "{name}"',
    },
    [AUDIT_TYPE.CREATE_CALENDAR]: {
      text: "Calendar created",
      subtext: '{userFullName} created Calendar "{name}"',
    },
    [AUDIT_TYPE.UPDATE_CALENDAR]: {
      text: "Calendar updated",
      subtext: '{userFullName} updated Calendar "{name}"',
    },
    [AUDIT_TYPE.DELETE_CALENDAR]: {
      text: "Calendar deleted",
      subtext: '{userFullName} deleted Calendar "{name}"',
    },
    [AUDIT_TYPE.CREATE_ABSENCE]: {
      text: "Type of Absence created",
      subtext: '{userFullName} created type of absence "{name}"',
    },
    [AUDIT_TYPE.UPDATE_ABSENCE]: {
      text: "Type of Absence changed",
      subtext: '{userFullName} changed type of absence "{name}"',
    },

    [AUDIT_TYPE.CREATE_ASSIGNMENT]: {
      text: "Calendar assignment created",
      subtext: "{userFullName} assigned an employee for planning",
    },
    [AUDIT_TYPE.CREATE_ASSIGNMENT + "_PROJECT"]: {
      subtext: '{userFullName} assigned employee "{employee}" the project "{project}"',
    },
    [AUDIT_TYPE.CREATE_ASSIGNMENT + "_PHASE"]: {
      subtext: '{userFullName} assigned employee "{employee}" the project "{project}" and phase "{phase}"',
    },
    [AUDIT_TYPE.CREATE_ASSIGNMENT + "_ABSENCE"]: {
      subtext: '{userFullName} assigned employee "{employee}" the absence "{absence}"',
    },
    [AUDIT_TYPE.UPDATE_ASSIGNMENT]: {
      text: "Calendar assignment updated",
      subtext: "{userFullName} updated employee assignment for planning",
    },
    [AUDIT_TYPE.UPDATE_ASSIGNMENT + "_PROJECT"]: {
      subtext: '{userFullName} updated employee "{employee}" with project "{project}"',
    },
    [AUDIT_TYPE.UPDATE_ASSIGNMENT + "_PHASE"]: {
      subtext: '{userFullName} updated employee "{employee}" with project "{project}" and phase "{phase}"',
    },
    [AUDIT_TYPE.UPDATE_ASSIGNMENT + "_ABSENCE"]: {
      subtext: '{userFullName} updated employee "{employee}" with absence "{absence}"',
    },
    [AUDIT_TYPE.DELETE_ASSIGNMENT]: {
      text: "Calendar assignment deleted",
      subtext: "{userFullName} removed employee assignment in the planning",
    },
    [AUDIT_TYPE.DELETE_ASSIGNMENT + "_PROJECT"]: {
      subtext: '{userFullName} removed assignment for employee "{employee}" with project "{project}"',
    },
    [AUDIT_TYPE.DELETE_ASSIGNMENT + "_PHASE"]: {
      subtext:
        '{userFullName} removed assignment for employee "{employee}" with project "{project}" and phase "{phase}"',
    },
    [AUDIT_TYPE.DELETE_ASSIGNMENT + "_ABSENCE"]: {
      subtext: '{userFullName} removed assignment for employee "{employee}" with absence "{absence}"',
    },

    // Time tracking. The server sends no payload for a time entry, so the subtexts name neither the
    // employee nor the day — both are in the property lines below the row.
    [AUDIT_TYPE.CREATE_TIME_ENTRY]: {
      text: "Time recorded",
      subtext: "{userFullName} recorded a time entry",
    },
    [AUDIT_TYPE.UPDATE_TIME_ENTRY]: {
      text: "Time updated",
      subtext: "{userFullName} updated a recorded time entry",
    },
    [AUDIT_TYPE.DELETE_TIME_ENTRY]: {
      text: "Time deleted",
      subtext: "{userFullName} deleted a recorded time entry",
    },
    [AUDIT_TYPE.CREATE_WORK_TIME_MODEL]: {
      text: "Working-time model created",
      subtext: "{userFullName} created a working-time model",
    },
    [AUDIT_TYPE.UPDATE_WORK_TIME_MODEL]: {
      text: "Working-time model updated",
      subtext: "{userFullName} updated a working-time model",
    },
    [AUDIT_TYPE.ARCHIVE_WORK_TIME_MODEL]: {
      text: "Working-time model archived",
      subtext: "{userFullName} archived a working-time model",
    },
    [AUDIT_TYPE.RESTORE_WORK_TIME_MODEL]: {
      text: "Working-time model restored",
      subtext: "{userFullName} restored a working-time model",
    },
    [AUDIT_TYPE.DELETE_WORK_TIME_MODEL]: {
      text: "Working-time model deleted",
      subtext: "{userFullName} deleted a working-time model",
    },
    [AUDIT_TYPE.CREATE_PDF_LETTERHEAD]: {
      text: "Letterhead created",
      subtext: "{userFullName} created a letterhead",
    },
    [AUDIT_TYPE.UPDATE_PDF_LETTERHEAD]: {
      text: "Letterhead changed",
      subtext: "{userFullName} changed a letterhead",
    },
    [AUDIT_TYPE.DELETE_PDF_LETTERHEAD]: {
      text: "Letterhead deleted",
      subtext: "{userFullName} deleted a letterhead",
    },
    [AUDIT_TYPE.CREATE_PDF_TEMPLATE]: {
      text: "PDF template created",
      subtext: "{userFullName} created a PDF template",
    },
    [AUDIT_TYPE.UPDATE_PDF_TEMPLATE]: {
      text: "PDF template changed",
      subtext: "{userFullName} changed a PDF template",
    },
    [AUDIT_TYPE.DELETE_PDF_TEMPLATE]: {
      text: "PDF template deleted",
      subtext: "{userFullName} deleted a PDF template",
    },
    [AUDIT_TYPE.CREATE_DATA_EXPORT]: {
      text: "Data export requested",
      subtext: "{userFullName} requested a complete copy of all company data",
    },
    // The speech recognition's vocabulary. The payload names the entry's terms; older entries have none and
    // name no term.
    [AUDIT_TYPE.CREATE_VOICE_VOCABULARY]: {
      text: "Vocabulary entry created",
      subtext: "{userFullName} added an entry to the speech recognition's vocabulary",
      subtext_named: '{userFullName} added "{name}" to the speech recognition\'s vocabulary',
    },
    [AUDIT_TYPE.UPDATE_VOICE_VOCABULARY]: {
      text: "Vocabulary entry updated",
      subtext: "{userFullName} updated an entry of the speech recognition's vocabulary",
      subtext_named: '{userFullName} updated the entry "{name}" of the speech recognition\'s vocabulary',
    },
    [AUDIT_TYPE.DELETE_VOICE_VOCABULARY]: {
      text: "Vocabulary entry deleted",
      subtext: "{userFullName} deleted an entry from the speech recognition's vocabulary",
      subtext_named: '{userFullName} deleted "{name}" from the speech recognition\'s vocabulary',
    },
    [AUDIT_TYPE.CREATE_DAY_SUBMISSION]: {
      text: "Day submission sent",
      subtext: "{userFullName} sent {date} on {project} to the site management",
    },
    [AUDIT_TYPE.UPDATE_DAY_SUBMISSION]: {
      text: "Day submission edited",
      subtext: "{userFullName} edited the day submission of {sender} for {date} ({project})",
    },
    [AUDIT_TYPE.DISMISS_DAY_SUBMISSION]: {
      text: "Day submission dismissed",
      subtext: "{userFullName} dismissed the day submission of {sender} for {date} ({project})",
    },
    [AUDIT_TYPE.REOPEN_DAY_SUBMISSION]: {
      text: "Day submission reopened",
      subtext: "{userFullName} reopened the day submission of {sender} for {date} ({project})",
    },
    [AUDIT_TYPE.CONVERT_DAY_SUBMISSION]: {
      text: "Day submission taken over",
      subtext: "{userFullName} took over the day submission of {sender} for {date} ({project}) as a report",
    },
  },
}
